import React, { useState } from "react";
import { requestPasswordReset } from "../../../helpers/authHelpers";
import { devLog } from "../../../helpers/devLog";
import { getActorRole } from "../../../helpers/permissions";
import { saveEmployeePhone } from "../../../helpers/admin/usersAdmin";
import { AdminFormCard } from "../shared/AdminFormCard";
import { AdminLegacyHeader } from "../shared/AdminFormCard";
import { CPInput } from "../../CPInput";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import "../adminDesktop.css";

export function AdminPasswordResetSuccessScreen({ email, onBack }) {
  return (
    <div className="screen nx-screen ci-success-screen">
      <div className="ci-success-content">
        <div className="ci-success-icon">
          <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle
              className="checkmark-circle nx-checkmark-circle"
              cx="26"
              cy="26"
              r="24"
            />
            <path
              className="checkmark-check nx-checkmark-check"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
            />
          </svg>
        </div>

        <h1 className="ci-success-title">Password Reset Sent</h1>

        <p className="ci-success-sub">Reset instructions have been sent to:</p>

        <p
          style={{
            color: "#c9a84c",
            fontWeight: 600,
            marginBottom: "16px",
          }}
        >
          {email}
        </p>

        <button type="button" className="nx-btn nx-btn-gold" onClick={onBack}>
          Back to Profile
        </button>
      </div>
    </div>
  );
}

export function AdminEmployeeProfilePanel({ currentUser, onBack, showLegacyHeader = true }) {
  const isDesktop = useIsDesktopViewport();
  const [phone, setPhone] = useState(currentUser?.phone || "");
  const [saving, setSaving] = useState(false);
  const [showPasswordResetSuccess, setShowPasswordResetSuccess] = useState(false);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await saveEmployeePhone({
        supabase_user_id: currentUser.supabase_user_id,
        phone,
      });
      alert("Profile updated successfully");
    } catch (err) {
      console.error("PROFILE SAVE ERROR:", err);
      alert("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      devLog("[AUTH DEBUG] AdminEmployeeProfilePanel: sending reset link");
      const { error } = await requestPasswordReset(currentUser.email);
      if (error) throw error;
      setShowPasswordResetSuccess(true);
    } catch (err) {
      console.error("PASSWORD RESET ERROR:", err);
      alert("Failed to send password reset email.");
    }
  };

  if (showPasswordResetSuccess) {
    return (
      <AdminPasswordResetSuccessScreen
        email={currentUser?.email}
        onBack={() => setShowPasswordResetSuccess(false)}
      />
    );
  }

  const desktopContent = (
    <div className="adm-main-side">
      <AdminFormCard title="CONTACT">
        <div className="adm-fields adm-fields--1">
          <CPInput
            label="Phone"
            type="tel"
            value={phone}
            onChange={setPhone}
            placeholder="Enter phone number"
          />
        </div>
        <div className="adm-actions">
          <button
            type="button"
            className="kpi-back-btn adm-primary"
            onClick={
              currentUser?.isGuest
                ? () => alert("Profile editing available in full version.")
                : handleSaveProfile
            }
          >
            {currentUser?.isGuest
              ? "Available In Full Version"
              : saving
                ? "Saving..."
                : "Save Changes"}
          </button>
          {!currentUser?.isGuest && (
            <button type="button" className="kpi-back-btn" onClick={handlePasswordReset}>
              Reset Password
            </button>
          )}
        </div>
      </AdminFormCard>
      <AdminFormCard title="EMPLOYEE PROFILE">
        <dl className="adm-kv">
          <dt>Name</dt>
          <dd>{currentUser?.employee_name || "Not Available"}</dd>
          <dt>Email</dt>
          <dd>{currentUser?.email || "Not Available"}</dd>
          <dt>Role</dt>
          <dd>{getActorRole(currentUser) || "Not Available"}</dd>
          <dt>Trade</dt>
          <dd>{currentUser?.trade_name || "Not Assigned"}</dd>
          <dt>Class</dt>
          <dd>{currentUser?.classification_name || "Not Assigned"}</dd>
        </dl>
      </AdminFormCard>
    </div>
  );

  const content = isDesktop ? desktopContent : (
    <AdminFormCard title="EMPLOYEE PROFILE">
      <div className="profile-grid">
        <div className="section-heading">NAME</div>
        <p className="profile-value">
          {currentUser?.employee_name || "Not Available"}
        </p>

        <div className="section-heading">EMAIL</div>
        <p className="profile-value">{currentUser?.email || "Not Available"}</p>

        <div className="section-heading">ROLE</div>
        <p className="profile-value">{getActorRole(currentUser) || "Not Available"}</p>

        <div className="section-heading">TRADE</div>
        <p className="profile-value">{currentUser?.trade_name || "Not Assigned"}</p>

        <div className="section-heading">CLASSIFICATION</div>
        <p className="profile-value">
          {currentUser?.classification_name || "Not Assigned"}
        </p>

        <CPInput
          label="Phone"
          type="tel"
          value={phone}
          onChange={setPhone}
          placeholder="Enter phone number"
        />

        <button
          type="button"
          className="kpi-back-btn"
          onClick={
            currentUser?.isGuest
              ? () => alert("Profile editing available in full version.")
              : handleSaveProfile
          }
        >
          {currentUser?.isGuest
            ? "Available In Full Version"
            : saving
              ? "Saving..."
              : "Save Changes"}
        </button>

        {!currentUser?.isGuest && (
          <button
            type="button"
            className="kpi-back-btn"
            style={{ marginTop: "10px" }}
            onClick={handlePasswordReset}
          >
            Reset Password
          </button>
        )}
      </div>
    </AdminFormCard>
  );

  if (showLegacyHeader) {
    return (
      <div className="intel-screen">
        <AdminLegacyHeader title="EMPLOYEE PROFILE" onBack={onBack} />
        {content}
      </div>
    );
  }

  return content;
}
